import { ColumnsType, Text } from '@agro-club/agroclub-shared'
import { REACT_APP_CURRENCY_SYMBOL } from 'env'
import useFormatDate from 'hooks/useFormatDate'
import { TFunction } from 'i18next'
import { AuthContext } from 'modules/context/AuthContext'
import { convertSortingToAntDFormat } from 'modules/domain/common/tableUtils'
import { Company } from 'modules/domain/company/types'
import { ListRequestParams } from 'modules/domain/types'
import { CompanyMembersTypes } from 'modules/domain/user/types'
import formatNumber from 'modules/utils/numbers/formatNumber'
import formatPrice from 'modules/utils/numbers/formatPrice'
import { useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { CompanyLink } from 'views/components/CompanyLink/CompanyLink'
import { DealLink } from 'views/components/DealLink/DealLink'
import { DispatchingStatusesTags } from 'views/components/DispatchingStatusesTags/DispatchingStatusesTags'
import { CompanyFactoringIcon } from 'views/components/TooltipIcons/TooltipIcons'
import { TruncatedValue } from 'views/components/TruncatedValue/TruncatedValue'
import { HeaderCellSubtitle } from 'views/pages/Dispatching/styled'
import { EntityWithIconsWrapper } from 'views/styled/common'
import { calculateColumnSum } from './ helpers'
import { CommentsCell } from './components/CommentsCell/CommentsCell'
import { EditInfoCell } from './components/EditInfoCell'
import { LogisticianCell } from './components/LogisticianCell'
import { SpecificationLink } from './components/SpecificationLink'
import { TripsLinkCell } from './components/TripsLinkCell'
import { getColors, renderPaymentsCell } from './helpers'
import { DispatchingItem } from './types'

export const renderCompanyDetails = (company: Company, profileType: CompanyMembersTypes, addressTitle?: string) => {
  return (
    <>
      <EntityWithIconsWrapper>
        <TruncatedValue hint={company.name || ''}>
          <CompanyLink company={company} target="_blank" bodysize="bodyMedium" />
        </TruncatedValue>
        <CompanyFactoringIcon company={company} profileType={profileType} />
      </EntityWithIconsWrapper>
      {addressTitle && (
        <TruncatedValue hint={addressTitle}>
          <Text typography="bodySmall" color="secondary">
            {addressTitle}
          </Text>
        </TruncatedValue>
      )}
    </>
  )
}

const getColumns = (
  datesArray: string[],
  t: TFunction,
  permissions: string[],
  data: object[],
  onSuccess: () => void,
  setDrawerIsOpen: (arg: boolean) => void,
  setSelectedDealId: (value: string) => void,
  sortParam: ListRequestParams['sort'],
  formatDate: any,
) => {
  const currency = REACT_APP_CURRENCY_SYMBOL

  const datesColumns = datesArray
    ? datesArray.map((date) => ({
        key: date,
        title: () => (
          <>
            <div>{`${formatDate(date, { dateFormat: 'ddMM' })}, ${currency}`}</div>
            <HeaderCellSubtitle>
              {formatNumber(calculateColumnSum(data, `payments.${date}.amount`), true, { fractionDigits: 3 })}
            </HeaderCellSubtitle>
          </>
        ),
        width: 150,
        align: 'right',
        dataIndex: 'payments',
        render: (payments: DispatchingItem['payments']) => {
          return renderPaymentsCell(payments, date, t)
        },
        onCell: (record) => {
          const colors = getColors(record.payments[date]?.status)
          return { style: { backgroundColor: colors.background } }
        },
        onHeaderCell: () => ({
          withSubTitle: true,
        }),
      }))
    : []

  const results = [
    {
      title: t('table.product'),
      width: 160,
      dataIndex: 'product_title',
      render: (product_title, data) => (
        <DealLink dealId={data.deal_id} bodysize="bodyMedium" target="_blank">
          {product_title}
        </DealLink>
      ),
      fixed: 'left',
    },
    {
      title: t('table.sellerAndCustomer'),
      width: 215,
      dataIndex: 'customer',
      sorter: true,
      sortOrder: convertSortingToAntDFormat('customer', sortParam),
      render: (customer, data) => (
        <>
          {renderCompanyDetails(data.seller, CompanyMembersTypes.seller, data.seller_address_title)}
          {renderCompanyDetails(customer, CompanyMembersTypes.customer, data.customer_address_title)}
        </>
      ),
      fixed: 'left',
    },
    {
      title: t('table.status'),
      width: 160,
      dataIndex: 'status',
      render: (status) => <DispatchingStatusesTags status={status} />,
    },
    {
      title: t('table.price_exw', { currency }),
      width: 72,
      align: 'right',
      dataIndex: 'price_exw',
      render: (price_exw) => formatPrice(price_exw),
    },
    {
      title: () => (
        <>
          {t('table.cost', { currency })}
          <HeaderCellSubtitle>{formatNumber(calculateColumnSum(data, 'cost'))}</HeaderCellSubtitle>
        </>
      ),
      width: 130,
      align: 'right',
      dataIndex: 'cost',
      render: (cost) => formatNumber(cost),
      onHeaderCell: () => ({
        withSubTitle: true,
      }),
    },
    {
      title: () => (
        <>
          {t('table.quantity')}
          <HeaderCellSubtitle>{formatNumber(calculateColumnSum(data, 'quantity'))}</HeaderCellSubtitle>
        </>
      ),
      width: 94,
      align: 'right',
      dataIndex: 'quantity',
      render: (quantity) => formatNumber(quantity, true),
      onHeaderCell: () => ({
        withSubTitle: true,
      }),
    },
    {
      title: () => (
        <>
          {t('table.quantity_paid_for')}
          <HeaderCellSubtitle>{formatNumber(calculateColumnSum(data, 'quantity_paid_for'))}</HeaderCellSubtitle>
        </>
      ),
      width: 105,
      align: 'right',
      dataIndex: 'quantity_paid_for',
      render: (quantity_paid_for) => formatNumber(quantity_paid_for, true),
      onHeaderCell: () => ({
        withSubTitle: true,
      }),
    },
    {
      title: () => (
        <>
          {t('table.loaded_quantity')}
          <HeaderCellSubtitle>{formatNumber(calculateColumnSum(data, 'loaded_quantity'))}</HeaderCellSubtitle>
        </>
      ),
      width: 110,
      align: 'right',
      dataIndex: 'loaded_quantity',
      render: (loaded_quantity) => formatNumber(loaded_quantity, true),
      onHeaderCell: () => ({
        withSubTitle: true,
      }),
    },
    {
      title: () => (
        <>
          {t('table.delivered_quantity')}
          <HeaderCellSubtitle>{formatNumber(calculateColumnSum(data, 'delivered_quantity'))}</HeaderCellSubtitle>
        </>
      ),
      width: 110,
      align: 'right',
      dataIndex: 'delivered_quantity',
      render: (delivered_quantity) => formatNumber(delivered_quantity, true),
      onHeaderCell: () => ({
        withSubTitle: true,
      }),
    },
    {
      title: () => (
        <>
          {t('table.remaining_quantity')}
          <HeaderCellSubtitle>{formatNumber(calculateColumnSum(data, 'remaining_quantity'))}</HeaderCellSubtitle>
        </>
      ),
      width: 100,
      align: 'right',
      dataIndex: 'remaining_quantity',
      render: (remaining_quantity) => formatNumber(remaining_quantity, true),
      onHeaderCell: () => ({
        withSubTitle: true,
      }),
    },
    {
      title: () => (
        <>
          {t('table.quantity_logistics')}
          <HeaderCellSubtitle>{formatNumber(calculateColumnSum(data, 'quantity_logistics'))}</HeaderCellSubtitle>
        </>
      ),
      width: 150,
      align: 'right',
      dataIndex: 'quantity_logistics',
      render: (quantity_logistics) => formatNumber(quantity_logistics, true),
      onHeaderCell: () => ({
        withSubTitle: true,
      }),
    },
    {
      title: () => (
        <>
          {t('table.planned_remaining_quantity')}
          <HeaderCellSubtitle>
            {formatNumber(calculateColumnSum(data, 'planned_remaining_quantity'))}
          </HeaderCellSubtitle>
        </>
      ),
      width: 150,
      align: 'right',
      dataIndex: 'planned_remaining_quantity',
      render: (planned_remaining_quantity) => formatNumber(planned_remaining_quantity, true),
      onHeaderCell: () => ({
        withSubTitle: true,
      }),
    },
    {
      title: () => (
        <>
          {t('table.trips_today')}
          <HeaderCellSubtitle>{formatNumber(calculateColumnSum(data, 'trips_today'))}</HeaderCellSubtitle>
        </>
      ),
      width: 126,
      align: 'right',
      dataIndex: 'trips_today',
      render: (trips_today, data) => (
        <TripsLinkCell value={trips_today} logisticsRequestId={data.logistics_request_id} />
      ),
      onHeaderCell: () => ({
        withSubTitle: true,
      }),
    },
    {
      title: () => (
        <>
          {t('table.trips_expected')}
          <HeaderCellSubtitle>{formatNumber(calculateColumnSum(data, 'trips_expected'))}</HeaderCellSubtitle>
        </>
      ),
      width: 130,
      align: 'right',
      dataIndex: 'trips_expected',
      render: (trips_expected, data) => (
        <TripsLinkCell value={trips_expected ?? 0} logisticsRequestId={data.logistics_request_id} />
      ),
      onHeaderCell: () => ({
        withSubTitle: true,
      }),
    },
    {
      title: () => (
        <>
          {t('table.trips_tomorrow')}
          <HeaderCellSubtitle>{formatNumber(calculateColumnSum(data, 'trips_tomorrow'))}</HeaderCellSubtitle>
        </>
      ),
      width: 120,
      align: 'right',
      dataIndex: 'trips_tomorrow',
      render: (trips_tomorrow, data) => (
        <TripsLinkCell value={trips_tomorrow} logisticsRequestId={data.logistics_request_id} />
      ),
      onHeaderCell: () => ({
        withSubTitle: true,
      }),
    },
    {
      title: () => (
        <>
          {t('table.can_be_delivered')}
          <HeaderCellSubtitle>
            {formatNumber(calculateColumnSum(data, 'trips_available'))} (
            {formatNumber(calculateColumnSum(data, 'trips_planned_payment_available'))})
          </HeaderCellSubtitle>
        </>
      ),
      width: 196,
      dataIndex: 'trips_available',
      align: 'right',
      render: (trips_available, data) => `${trips_available} (${data.trips_planned_payment_available})`,
      onHeaderCell: () => ({
        withSubTitle: true,
      }),
    },
    {
      title: t('table.logisticians'),
      width: 260,
      dataIndex: 'logistician',
      render: (_, data) => (
        <LogisticianCell data={data} permissions={permissions} deal_id={data.deal_id} onSuccess={onSuccess} />
      ),
    },
    {
      title: t('table.comments'),
      width: 280,
      dataIndex: 'comments',
      render: (comments, data) => (
        <CommentsCell
          comments={comments}
          dealId={data.deal_id}
          setDrawerIsOpen={setDrawerIsOpen}
          setSelectedDealId={setSelectedDealId}
        />
      ),
    },
    {
      title: t('table.specification_deadline'),
      width: 100,
      dataIndex: 'purchase_specification_deadline',
      render: (purchase_specification_deadline, data) => (
        <SpecificationLink
          deadline={purchase_specification_deadline}
          id={data.purchase_specification_id}
          without_purchase_specification={data.without_purchase_specification}
        />
      ),
    },
    {
      title: t('table.documents_request_expected_complete_date'),
      width: 100,
      dataIndex: 'documents_request_expected_complete_date',
      render: (documents_request_expected_complete_date, data) => {
        if (data.without_documents_request) return t('common:without_docs')
        return formatDate(documents_request_expected_complete_date)
      },
    },
    ...datesColumns,
    {
      title: () => (
        <>
          {t('table.remaining_cost', { currency })}
          <HeaderCellSubtitle>{formatNumber(calculateColumnSum(data, 'remaining_cost', false))}</HeaderCellSubtitle>
        </>
      ),
      width: 130,
      align: 'right',
      dataIndex: 'remaining_cost',
      render: (remaining_cost) => formatNumber(remaining_cost),
      onHeaderCell: () => ({
        withSubTitle: true,
      }),
    },
    {
      title: '',
      width: 50,
      dataIndex: 'id',
      align: 'center',
      fixed: 'right',
      render: (id) => (
        <EditInfoCell id={id} permissions={permissions} onSuccess={onSuccess} currentDates={datesArray} />
      ),
    },
  ] as ColumnsType<any>

  return results
}

const useColumns = (
  datesArray: string[],
  data: object[],
  onSuccess: () => void,
  setDrawerIsOpen: (arg: boolean) => void,
  setSelectedDealId: (value: string) => void,
  sortParam: ListRequestParams['sort'],
): ColumnsType<any> => {
  const { t } = useTranslation('dispatching')
  const { profile } = useContext(AuthContext)
  const formatDate = useFormatDate()
  const permissions = profile?.permissions ?? []

  return useMemo(
    () =>
      getColumns(
        datesArray,
        t,
        permissions,
        data,
        onSuccess,
        setDrawerIsOpen,
        setSelectedDealId,
        sortParam,
        formatDate,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, datesArray, permissions, data, onSuccess, sortParam],
  )
}

export default useColumns
